import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthError } from '../errors/auth.error';
import { AuthService } from '../services/auth.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-revert-mfa',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatCheckboxModule, MatFormFieldModule, MatProgressSpinnerModule, RouterLink, TranslatePipe],
  templateUrl: './revert-mfa.component.html',
  styleUrl: './revert-mfa.component.scss',
})
export class RevertMfaComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly loading = signal(true);
  protected readonly applying = signal(false);
  protected readonly completed = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly factorName = signal('TOTP');
  protected readonly form = this.formBuilder.nonNullable.group({
    confirmation: ['', Validators.required],
  });

  private actionCode: string | null = null;

  ngOnInit(): void {
    this.actionCode = this.route.snapshot.queryParamMap.get('oobCode');
    this.loadAction().catch(console.error);
  }

  protected async confirm(): Promise<void> {
    if (this.form.invalid || !this.actionCode) {
      this.form.markAllAsTouched();
      return;
    }

    this.applying.set(true);
    this.errorMessage.set(null);
    try {
      await this.authService.applyActionCode(this.actionCode);
      this.completed.set(true);
    } catch (error: unknown) {
      this.errorMessage.set(error instanceof AuthError ? error.message : 'autherror.default');
    } finally {
      this.applying.set(false);
    }
  }

  private async loadAction(): Promise<void> {
    if (!this.actionCode) {
      this.loading.set(false);
      this.errorMessage.set('autherror.invalid.actioncode');
      return;
    }

    try {
      const action = await this.authService.checkActionCode(this.actionCode);
      if (action.operation !== 'REVERT_SECOND_FACTOR_ADDITION') {
        this.errorMessage.set('autherror.invalid.actioncode');
        return;
      }

      this.factorName.set(action.data.multiFactorInfo?.displayName || 'TOTP');
    } catch (error: unknown) {
      this.errorMessage.set(error instanceof AuthError ? error.message : 'autherror.default');
    } finally {
      this.loading.set(false);
    }
  }
}
